export const DocsConfig = {
    // Getting Started
    "user-guide": {
        name: "User Guide",
        slug: "user-guide",
        tagline: "Getting Started with TurboSteed",
        description: "Complete guide to using TurboSteed — from creating your first site to managing content via the dashboard.",
        mdFile: "user-guide.md",
        icon: "fa fa-book",
        category: "Getting Started",
        order: 1,
        seo: {
            title: "User Guide | TurboSteed Documentation",
            description: "Complete beginner's guide to TurboSteed. Learn how to create sites, upload code, manage CMS content, configure domains, view analytics, and use the mobile app.",
            canonicalUrl: "https://turbosteed.com/docs/user-guide",
            keywords: "TurboSteed tutorial, web hosting guide, CMS guide, site deployment, custom domains, contact forms, analytics dashboard",
            ogType: "article",
            ogTitle: "TurboSteed User Guide - Getting Started",
            ogDescription: "Everything you need to know to get started with TurboSteed. Create sites, manage content, configure domains, and more.",
            ogImage: "https://turbosteed.com/images/ts-social-share-docs.jpg",
            twitterCard: "summary_large_image",
            robots: "index, follow"
        },
        schema: {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": "TurboSteed User Guide",
            "description": "Complete beginner's guide to using TurboSteed for web hosting and content management.",
            "datePublished": "2026-09-10",
            "dateModified": "2026-09-11",
            "author": { "@type": "Organization", "name": "TurboSteed" },
            "publisher": {
                "@type": "Organization",
                "name": "TurboSteed",
                "logo": { "@type": "ImageObject", "url": "https://turbosteed.com/turbo-navbar-logo.png" }
            },
            "mainEntityOfPage": { "@type": "WebPage", "@id": "https://turbosteed.com/docs/user-guide" }
        }
    },

    "platform-features": {
        name: "Platform Features",
        slug: "platform-features",
        tagline: "What TurboSteed Offers",
        description: "Overview of TurboSteed's hosting features — CDN, SSL, analytics, scalability, and more.",
        mdFile: "platform-features.md",
        icon: "fa fa-server",
        category: "Getting Started",
        order: 2,
        seo: {
            title: "Platform Features | TurboSteed Documentation",
            description: "Explore TurboSteed's hosting features: global CDN, free SSL, built-in analytics, scalable infrastructure, contact forms, asset library, and API access.",
            canonicalUrl: "https://turbosteed.com/docs/platform-features",
            keywords: "TurboSteed features, CDN hosting, SSL certificate, web hosting features, scalable hosting, analytics dashboard",
            ogType: "article",
            ogTitle: "TurboSteed Platform Features",
            ogDescription: "Enterprise-grade hosting features without the complexity. CDN, SSL, analytics, and more.",
            ogImage: "https://turbosteed.com/images/ts-social-share-docs.jpg",
            twitterCard: "summary_large_image",
            robots: "index, follow"
        },
        schema: {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": "TurboSteed Platform Features",
            "description": "Overview of TurboSteed's hosting features including CDN, SSL, analytics, and scalability.",
            "datePublished": "2026-09-11",
            "dateModified": "2026-09-11",
            "author": { "@type": "Organization", "name": "TurboSteed" },
            "publisher": {
                "@type": "Organization",
                "name": "TurboSteed",
                "logo": { "@type": "ImageObject", "url": "https://turbosteed.com/turbo-navbar-logo.png" }
            },
            "mainEntityOfPage": { "@type": "WebPage", "@id": "https://turbosteed.com/docs/platform-features" }
        }
    },

    "templates": {
        name: "Site Templates",
        slug: "templates",
        tagline: "Pre-designed Website Templates",
        description: "Use pre-built templates to create websites without coding. Customize content, colors, and SEO through the visual editor.",
        mdFile: "templates.md",
        icon: "fa fa-paint-brush",
        category: "Getting Started",
        order: 3,
        seo: {
            title: "Site Templates | TurboSteed Documentation",
            description: "Create websites without coding using TurboSteed templates. Customize text, images, colors, and SEO settings through the visual editor.",
            canonicalUrl: "https://turbosteed.com/docs/templates",
            keywords: "website templates, site templates, visual editor, no-code website, template customization, SEO settings",
            ogType: "article",
            ogTitle: "TurboSteed Site Templates",
            ogDescription: "Pre-designed website templates with visual customization. Create professional sites without coding.",
            ogImage: "https://turbosteed.com/images/ts-social-share-docs.jpg",
            twitterCard: "summary_large_image",
            robots: "index, follow"
        },
        schema: {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": "TurboSteed Site Templates",
            "description": "Guide to using TurboSteed's pre-designed website templates.",
            "datePublished": "2026-09-11",
            "dateModified": "2026-09-11",
            "author": { "@type": "Organization", "name": "TurboSteed" },
            "publisher": {
                "@type": "Organization",
                "name": "TurboSteed",
                "logo": { "@type": "ImageObject", "url": "https://turbosteed.com/turbo-navbar-logo.png" }
            },
            "mainEntityOfPage": { "@type": "WebPage", "@id": "https://turbosteed.com/docs/templates" }
        }
    },

    // Configuration
    "custom-domains": {
        name: "Custom Domains",
        slug: "custom-domains",
        tagline: "Connect Your Own Domain",
        description: "Step-by-step guide to connecting your custom domain to TurboSteed with DNS configuration instructions.",
        mdFile: "custom-domains.md",
        icon: "fa fa-globe",
        category: "Configuration",
        order: 4,
        seo: {
            title: "Custom Domains Setup | TurboSteed Documentation",
            description: "Connect your custom domain to TurboSteed. Step-by-step DNS configuration guide with A record and CNAME setup instructions. Free SSL included.",
            canonicalUrl: "https://turbosteed.com/docs/custom-domains",
            keywords: "custom domain, DNS setup, A record, CNAME record, domain configuration, SSL certificate, domain hosting",
            ogType: "article",
            ogTitle: "Custom Domains Setup Guide",
            ogDescription: "Step-by-step guide to connecting your custom domain to TurboSteed with automatic SSL.",
            ogImage: "https://turbosteed.com/images/ts-social-share-docs.jpg",
            twitterCard: "summary_large_image",
            robots: "index, follow"
        },
        schema: {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": "Custom Domains Setup Guide",
            "description": "Step-by-step guide to connecting custom domains to TurboSteed.",
            "datePublished": "2026-09-11",
            "dateModified": "2026-09-11",
            "author": { "@type": "Organization", "name": "TurboSteed" },
            "publisher": {
                "@type": "Organization",
                "name": "TurboSteed",
                "logo": { "@type": "ImageObject", "url": "https://turbosteed.com/turbo-navbar-logo.png" }
            },
            "mainEntityOfPage": { "@type": "WebPage", "@id": "https://turbosteed.com/docs/custom-domains" }
        }
    },

    // Developer Guides
    "mcp-guide": {
        name: "MCP Integration Guide",
        slug: "mcp-guide",
        tagline: "Connect Claude Code to TurboSteed",
        description: "Configure MCP (Model Context Protocol) to let AI agents like Claude Code manage your sites, CMS, and assets.",
        mdFile: "mcp-integration-guide.md",
        icon: "fa fa-plug",
        category: "Developer Guides",
        order: 5,
        seo: {
            title: "MCP Integration Guide | TurboSteed Documentation",
            description: "Complete guide to integrating TurboSteed with Claude Code and AI agents via MCP (Model Context Protocol). Configure MCP, manage sites, CMS, assets, and webhooks through AI conversations.",
            canonicalUrl: "https://turbosteed.com/docs/mcp-guide",
            keywords: "MCP, Model Context Protocol, Claude Code, AI integration, TurboSteed API, AI site builder, Claude Desktop, AI agents",
            ogType: "article",
            ogTitle: "TurboSteed MCP Integration Guide - Connect AI Agents",
            ogDescription: "Learn how to connect Claude Code to TurboSteed via MCP. Manage sites, CMS content, assets, and deployments through natural conversation.",
            ogImage: "https://turbosteed.com/images/ts-social-share-docs.jpg",
            twitterCard: "summary_large_image",
            robots: "index, follow"
        },
        schema: {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": "TurboSteed MCP Integration Guide",
            "description": "Complete guide to integrating TurboSteed with Claude Code and AI agents via MCP.",
            "datePublished": "2026-09-10",
            "dateModified": "2026-09-11",
            "author": { "@type": "Organization", "name": "TurboSteed" },
            "publisher": {
                "@type": "Organization",
                "name": "TurboSteed",
                "logo": { "@type": "ImageObject", "url": "https://turbosteed.com/turbo-navbar-logo.png" }
            },
            "mainEntityOfPage": { "@type": "WebPage", "@id": "https://turbosteed.com/docs/mcp-guide" }
        }
    },

    "rest-api": {
        name: "REST API Reference",
        slug: "rest-api",
        tagline: "API Documentation",
        description: "Complete REST API reference for programmatic access to sites, CMS, assets, and forms.",
        icon: "fa fa-code",
        category: "Developer Guides",
        order: 6,
        external: true,
        url: "/docs/rest-api.html",
        seo: {
            title: "REST API Reference | TurboSteed Documentation",
            description: "Complete REST API documentation for TurboSteed. Endpoints for site management, CMS schemas and entries, asset uploads, contact forms, webhooks, and analytics.",
            canonicalUrl: "https://turbosteed.com/docs/rest-api.html",
            keywords: "TurboSteed API, REST API, web hosting API, CMS API, headless CMS, site management API, webhooks API",
            ogType: "article",
            ogTitle: "TurboSteed REST API Reference",
            ogDescription: "Complete API documentation for programmatic access to TurboSteed sites, CMS, assets, forms, and analytics.",
            ogImage: "https://turbosteed.com/images/ts-social-share-docs.jpg",
            twitterCard: "summary_large_image",
            robots: "index, follow"
        },
        schema: {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": "TurboSteed REST API Reference",
            "description": "Complete REST API documentation for TurboSteed web hosting platform.",
            "datePublished": "2026-09-01",
            "dateModified": "2026-09-11",
            "author": { "@type": "Organization", "name": "TurboSteed" },
            "publisher": {
                "@type": "Organization",
                "name": "TurboSteed",
                "logo": { "@type": "ImageObject", "url": "https://turbosteed.com/turbo-navbar-logo.png" }
            },
            "mainEntityOfPage": { "@type": "WebPage", "@id": "https://turbosteed.com/docs/rest-api.html" }
        }
    },

    // Support
    "faq": {
        name: "FAQ",
        slug: "faq",
        tagline: "Frequently Asked Questions",
        description: "Common questions about TurboSteed features, plans, quotas, and troubleshooting.",
        mdFile: "faq.md",
        icon: "fa fa-question-circle",
        category: "Support",
        order: 7,
        seo: {
            title: "FAQ | TurboSteed Documentation",
            description: "Frequently asked questions about TurboSteed. Find answers about plans, custom domains, quotas, CMS, contact forms, webhooks, and more.",
            canonicalUrl: "https://turbosteed.com/docs/faq",
            keywords: "TurboSteed FAQ, hosting FAQ, web hosting questions, CMS FAQ, contact forms FAQ, domain setup FAQ",
            ogType: "article",
            ogTitle: "TurboSteed FAQ",
            ogDescription: "Common questions and answers about TurboSteed features, plans, and usage.",
            ogImage: "https://turbosteed.com/images/ts-social-share-docs.jpg",
            twitterCard: "summary_large_image",
            robots: "index, follow"
        },
        schema: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "name": "TurboSteed FAQ",
            "description": "Frequently asked questions about TurboSteed web hosting platform.",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "What happens if I exhaust my bandwidth quota?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Your site will stop functioning until the end of the month. It will automatically resume at the beginning of the next month."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Can I use my custom domain while keeping my own email provider?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Yes! TurboSteed only requires A and CNAME records for web hosting. Your MX records for email remain under your control."
                    }
                }
            ]
        }
    }
};

// Categories for organizing docs on the home page
export const DocsCategories = [
    {
        name: "Getting Started",
        description: "Learn the basics of TurboSteed",
        order: 1
    },
    {
        name: "Configuration",
        description: "Set up domains and customize your site",
        order: 2
    },
    {
        name: "Developer Guides",
        description: "API, CLI, and MCP integration",
        order: 3
    },
    {
        name: "Support",
        description: "FAQ and troubleshooting",
        order: 4
    }
];

export const DocsHomeConfig = {
    title: "Documentation",
    subtitle: "Everything you need to build and manage sites with TurboSteed",
    seo: {
        title: "Documentation | TurboSteed",
        description: "TurboSteed documentation hub. User guides, MCP integration, REST API reference, custom domains setup, templates, and FAQ.",
        canonicalUrl: "https://turbosteed.com/docs",
        keywords: "TurboSteed documentation, MCP guide, REST API docs, web hosting docs, CMS documentation, AI integration, custom domains",
        ogType: "website",
        ogTitle: "TurboSteed Documentation",
        ogDescription: "Complete documentation for TurboSteed - user guides, API reference, MCP integration, and more.",
        ogImage: "https://turbosteed.com/images/ts-social-share-docs.jpg",
        twitterCard: "summary_large_image",
        robots: "index, follow"
    },
    schema: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "TurboSteed Documentation",
        "description": "Documentation hub for TurboSteed web hosting platform.",
        "url": "https://turbosteed.com/docs",
        "publisher": {
            "@type": "Organization",
            "name": "TurboSteed",
            "logo": { "@type": "ImageObject", "url": "https://turbosteed.com/turbo-navbar-logo.png" }
        }
    }
};
